import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PaperPlaneRight, ArrowLeft, ChatCircle, Crown, Lock, Timer, MagnifyingGlass } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { track } from '../lib/analytics';
import { SILHOUETTE_AVATAR, getAvatarUrl, getPhotoSrc } from '../lib/avatar';

const PLACEHOLDER_IMG = SILHOUETTE_AVATAR;

// ─── Last-Seen Formatter ───
function formatLastSeen(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 0) return 'just now';
    const s = Math.floor(diff / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `last seen ${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `last seen ${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `last seen ${d}d ago`;
    return `last seen ${new Date(iso).toLocaleDateString()}`;
  } catch { return ''; }
}

// ─── Countdown Timer Hook ───
function useCountdown(targetIso) {
  const [remaining, setRemaining] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!targetIso) { setRemaining(''); setDone(true); return; }
    const target = new Date(targetIso).getTime();

    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setRemaining('00:00:00'); setDone(true); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
      setDone(false);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetIso]);

  return { remaining, done };
}

// ─── Paywall Overlay ───
function ChatPaywall({ resetAt, onUpgrade }) {
  const { remaining, done } = useCountdown(resetAt);

  if (done) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="absolute inset-0 z-50 bg-[#060A1A]/90 backdrop-blur-md flex items-center justify-center p-6"
      data-testid="chat-paywall"
    >
      <div className="glass-card p-8 max-w-sm w-full text-center border border-[#D4AF37]/20">
        <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-5">
          <Lock size={28} weight="light" className="text-[#D4AF37]" />
        </div>
        <h3 className="text-xl font-semibold text-white font-['Outfit'] mb-2">Limit Reached!</h3>
        <p className="text-[#9EABCE] text-sm mb-5">
          You've used all 10 free conversations today.
        </p>

        {/* Countdown */}
        <div className="bg-white/5 rounded-2xl p-4 mb-5 border border-white/5">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Timer size={18} weight="light" className="text-[#D4AF37]" />
            <span className="text-xs text-[#9EABCE] uppercase tracking-wider font-bold">Free again in</span>
          </div>
          <div className="text-3xl font-bold text-[#D4AF37] font-mono tracking-wider" data-testid="paywall-countdown">
            {remaining}
          </div>
        </div>

        <p className="text-[#9EABCE] text-xs mb-5">
          Or upgrade now for <span className="text-[#D4AF37] font-bold">unlimited instant access</span>
        </p>

        <Link
          to="/premium"
          data-testid="paywall-upgrade-btn"
          className="w-full btn-gold py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 mb-3 block"
        >
          <Crown size={18} weight="fill" /> Upgrade to Premium
        </Link>
        <p className="text-[10px] text-[#9EABCE]/40">Starting at 5EUR/month with LAUNCH50</p>
      </div>
    </motion.div>
  );
}

// ─── Chat List Sidebar ───
function ChatList({ chats, selectedChat, onSelectChat, presenceMap }) {
  return (
    <div className="space-y-1" data-testid="chat-list">
      {chats.length === 0 ? (
        <div className="text-center py-12">
          <ChatCircle size={40} weight="light" className="text-[#9EABCE]/30 mx-auto mb-3" />
          <p className="text-[#9EABCE]/60 text-sm">No conversations yet</p>
          <p className="text-[#9EABCE]/40 text-xs mt-1">Start chatting from the Browse page</p>
        </div>
      ) : (
        chats.map(chat => {
          const other = chat.other_user || {};
          const photo = getAvatarUrl(other);
          const isActive = selectedChat === chat.id;
          const isOnline = !!presenceMap?.[other.id]?.online;

          return (
            <button
              key={chat.id}
              onClick={() => onSelectChat(chat)}
              data-testid={`chat-item-${chat.id}`}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
                isActive ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/20' : 'hover:bg-white/5'
              }`}
            >
              <div className="relative w-12 h-12 flex-shrink-0">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img src={photo} alt="" className="w-full h-full object-cover" />
                </div>
                {isOnline && (
                  <span
                    className="absolute bottom-0 right-0 flex h-3 w-3"
                    data-testid={`list-presence-pulse-${chat.id}`}
                  >
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 ring-2 ring-[#060A1A]" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm truncate ${isActive ? 'text-[#D4AF37]' : 'text-white'}`}>
                    {other.first_name || 'User'}
                  </span>
                  {other.is_verified && <span className="verified-badge" style={{ fontSize: '8px', padding: '1px 4px' }}>V</span>}
                  {isOnline && (
                    <span className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wide ml-auto flex-shrink-0">
                      Active now
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#9EABCE] truncate mt-0.5">{chat.last_message || 'Start chatting...'}</p>
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}

// ─── Chat Messages Area ───
function ChatMessages({ chat, currentUserId, onLimitHit, onChatCreated }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [sendError, setSendError] = useState('');
  const [presence, setPresence] = useState({ online: false, last_seen: null });
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  const other = chat?.other_user || {};
  const otherId = chat?.participants?.find(p => p !== currentUserId);

  // Poll online status for this recipient
  useEffect(() => {
    if (!otherId) return;
    let mounted = true;
    const fetchPresence = async () => {
      try {
        const { data } = await api.get('/users/online/status', { params: { user_ids: otherId } });
        if (!mounted) return;
        const s = data.statuses?.[otherId];
        if (s) setPresence({ online: !!s.online, last_seen: s.last_seen });
      } catch {}
    };
    fetchPresence();
    const iv = setInterval(fetchPresence, 20000);
    return () => { mounted = false; clearInterval(iv); };
  }, [otherId]);

  // Fetch message history
  const fetchMessages = useCallback(async () => {
    if (!chat) return;
    if (chat.pending) { setMessages([]); return; } // pending chat — no history yet
    try {
      const { data } = await api.get(`/chats/${chat.id}/messages`);
      setMessages(data.messages || []);
    } catch {}
  }, [chat]);

  // WebSocket connection — uses current origin for the WS URL
  useEffect(() => {
    if (!chat || !currentUserId) return;
    const token = localStorage.getItem('yublink_token');
    if (!token) return;

    const wsUrl = window.location.origin.replace(/^http/, 'ws') + `/api/ws/chat?token=${token}`;
    let ws;
    let pingInterval;

    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send ping every 25s to keep alive
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message' && data.chat_id === chat.id) {
            setMessages(prev => {
              if (prev.find(m => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          }
          if (data.type === 'typing' && data.sender_id !== currentUserId) {
            setTyping(true);
            setTimeout(() => setTyping(false), 2500);
          }
        } catch {}
      };

      ws.onclose = () => {
        // Reconnect after 3s
        setTimeout(() => {
          if (wsRef.current === ws) {
            // Trigger re-render to reconnect
          }
        }, 3000);
      };
    } catch {}

    return () => {
      if (pingInterval) clearInterval(pingInterval);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [chat, currentUserId]);

  // Load messages when chat changes
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  // Mark messages as read
  useEffect(() => {
    if (!chat || chat.pending) return;
    api.put(`/chats/${chat.id}/read`).catch(() => {});
  }, [chat, messages]);

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const otherId = chat.participants.find(p => p !== currentUserId);
      wsRef.current.send(JSON.stringify({ type: 'typing', recipient_id: otherId }));
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || sending) return;

    // Optimistic: clear input immediately
    setText('');
    setSending(true);
    setSendError('');

    try {
      const otherId = chat.participants.find(p => p !== currentUserId);
      console.log('[chat.send] recipient_id=', otherId, 'chat_id=', chat.id);
      const { data } = await api.post('/chats/send', { recipient_id: otherId, text: msg });
      track('message_sent', { recipient_id: otherId, chat_id: chat.id });
      // Add sent message (dedup with WebSocket)
      setMessages(prev => {
        if (prev.find(m => m.id === data.message.id)) return prev;
        return [...prev, data.message];
      });
      // If this was a pending chat, promote it to a real chat (id is returned from the backend)
      if (chat.pending && data.chat_id && onChatCreated) {
        onChatCreated({ ...chat, id: data.chat_id, pending: false, last_message: msg });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const otherId = chat.participants.find(p => p !== currentUserId);
      console.error('[chat.send] failed', { status, detail, recipient_id: otherId, chat_id: chat.id });
      // Check for chat limit paywall
      try {
        const parsed = JSON.parse(detail);
        if (parsed.type === 'chat_limit') {
          onLimitHit(parsed.reset_at);
          return;
        }
      } catch {}
      setSendError(typeof detail === 'string' ? detail : 'Failed to send message');
      // Restore the text so user doesn't lose it
      setText(msg);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (!chat) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <ChatCircle size={48} weight="light" className="text-[#9EABCE]/20 mx-auto mb-3" />
          <p className="text-[#9EABCE]/40 text-sm">Select a conversation</p>
        </div>
      </div>
    );
  }

  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <div className="flex-1 flex flex-col relative min-h-0" data-testid="chat-messages">
      {/* Header (sticky) */}
      <div className="sticky top-0 z-20 bg-[#060A1A]/95 backdrop-blur-md p-4 border-b border-white/10 flex items-center gap-3 shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
        <div className="relative w-10 h-10 flex-shrink-0">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img src={getAvatarUrl(other)} alt="" className="w-full h-full object-cover" />
          </div>
          {presence.online && (
            <span
              className="absolute bottom-0 right-0 flex h-3 w-3"
              data-testid="presence-online-pulse"
              aria-label="Online"
            >
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 ring-2 ring-[#060A1A]" />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-sm truncate">{other.first_name || 'User'}</span>
            {other.is_premium && <Crown size={14} weight="fill" className="text-[#D4AF37]" />}
            {other.is_verified && <span className="verified-badge" style={{ fontSize: '8px', padding: '1px 4px' }}>V</span>}
          </div>
          <p className="text-[11px] text-[#9EABCE]/70 truncate mt-0.5" data-testid="presence-status">
            {presence.online ? (
              <span className="text-emerald-400 font-medium">Online</span>
            ) : (
              formatLastSeen(presence.last_seen) || ' '
            )}
          </p>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-6 space-y-3">
        {messages.length === 0 && !typing && (
          <div className="flex items-center justify-center h-full py-12">
            <div className="text-center">
              <ChatCircle size={40} weight="light" className="text-[#9EABCE]/20 mx-auto mb-3" />
              <p className="text-[#9EABCE]/40 text-sm">
                {chat.pending ? `Say hi to ${other.first_name || 'your match'} 👋` : 'No messages yet'}
              </p>
            </div>
          </div>
        )}
        {messages.map((msg, idx) => {
          const isMine = msg.sender_id === currentUserId;
          const showTime = idx === 0 || (
            new Date(msg.created_at).getTime() - new Date(messages[idx - 1]?.created_at).getTime() > 300000
          );

          return (
            <div key={msg.id}>
              {showTime && (
                <div className="text-center my-5">
                  <span className="text-[10px] text-[#9EABCE]/50 bg-white/5 px-3 py-1 rounded-full">
                    {formatTime(msg.created_at)}
                  </span>
                </div>
              )}
              <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[75%] px-4 py-2.5 text-sm leading-relaxed ${
                    isMine
                      ? 'bg-[#D4AF37] text-[#060A1A] rounded-2xl rounded-br-md font-medium'
                      : 'bg-[#1E2A52] text-white rounded-2xl rounded-bl-md border border-white/15 shadow-[0_2px_10px_rgba(0,0,0,0.35)]'
                  }`}
                  data-testid={isMine ? 'msg-sent' : 'msg-received'}
                >
                  {msg.text}
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing indicator */}
        <AnimatePresence>
          {typing && (
            <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex justify-start">
              <div className="bg-[#1E2A52] border border-white/15 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5 shadow-[0_2px_10px_rgba(0,0,0,0.35)]">
                <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {sendError && (
        <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20 text-red-400 text-xs text-center" data-testid="chat-send-error">
          {sendError}
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSend}
        className="sticky bottom-0 z-30 p-3 border-t border-white/10 flex gap-2 items-center bg-[#060A1A]"
      >
        <Input
          ref={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); handleTyping(); }}
          placeholder="Type a message..."
          autoComplete="off"
          data-testid="chat-input"
          className="flex-1 bg-[#0B112B] border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-full h-11 px-5"
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          data-testid="chat-send-btn"
          className="w-11 h-11 rounded-full btn-gold flex items-center justify-center disabled:opacity-30 flex-shrink-0"
        >
          <PaperPlaneRight size={18} weight="fill" />
        </button>
      </form>
    </div>
  );
}

// ─── Main Chat Page ───
export default function ChatPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showList, setShowList] = useState(true);
  const [paywallResetAt, setPaywallResetAt] = useState(null);
  const [limitStatus, setLimitStatus] = useState(null);
  const [presenceMap, setPresenceMap] = useState({});
  const toUserId = searchParams.get('to');

  const fetchChats = useCallback(async () => {
    try {
      const { data } = await api.get('/chats');
      setChats(data.chats || []);
      return data.chats || [];
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLimitStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/chats/limit-status');
      setLimitStatus(data);
      if (data.locked && data.reset_at) {
        setPaywallResetAt(data.reset_at);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/auth'); return; }
    fetchLimitStatus();
    fetchChats().then(async (chatList) => {
      if (toUserId) {
        const existing = chatList.find(c => c.participants.includes(toUserId));
        if (existing) {
          setSelectedChat(existing);
          setShowList(false);
        } else {
          // DO NOT auto-send. Fetch the recipient and open an empty chat window.
          try {
            const { data } = await api.get(`/users/${toUserId}`);
            const otherUser = data.user || data;
            setSelectedChat({
              id: `pending-${toUserId}`,
              participants: [currentUser.id, toUserId],
              other_user: otherUser,
              pending: true
            });
            setShowList(false);
          } catch (err) {
            console.error('[chat.open-pending] failed', { recipient_id: toUserId, err });
          }
        }
      }
    });
  }, [currentUser, navigate, fetchChats, fetchLimitStatus, toUserId]);

  // Refresh chats when page becomes visible (mobile app-switching / tab focus)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchChats();
        fetchLimitStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchChats, fetchLimitStatus]);

  // Poll presence for all peers in the chat list (batched every 20s)
  useEffect(() => {
    const peerIds = chats.map(c => c.other_user?.id).filter(Boolean);
    if (peerIds.length === 0) return;
    let mounted = true;
    const fetchPresence = async () => {
      try {
        const { data } = await api.get('/users/online/status', { params: { user_ids: peerIds.join(',') } });
        if (mounted) setPresenceMap(data.statuses || {});
      } catch {}
    };
    fetchPresence();
    const iv = setInterval(fetchPresence, 20000);
    return () => { mounted = false; clearInterval(iv); };
  }, [chats]);

  const handleLimitHit = (resetAt) => {
    setPaywallResetAt(resetAt);
    fetchLimitStatus();
  };

  if (!currentUser) return null;

  return (
    <div className="min-h-screen pt-16 relative" data-testid="chat-page">
      {/* Paywall overlay */}
      {paywallResetAt && !currentUser.is_premium && (
        <ChatPaywall resetAt={paywallResetAt} />
      )}

      <div className="max-w-5xl mx-auto h-[calc(100vh-64px)] flex">
        {/* Sidebar */}
        <div className={`w-full md:w-80 md:block border-r border-white/5 flex flex-col ${showList ? 'block' : 'hidden md:flex'}`}>
          <div className="p-4 border-b border-white/5">
            <h2 className="text-lg font-semibold text-white font-['Outfit']">Messages</h2>
            {limitStatus && !limitStatus.is_premium && (
              <p className="text-xs text-[#9EABCE] mt-1">
                {limitStatus.locked
                  ? <span className="text-red-400">Limit reached</span>
                  : <>{limitStatus.chats_used}/10 conversations used</>
                }
              </p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <ChatList chats={chats} selectedChat={selectedChat?.id} presenceMap={presenceMap} onSelectChat={(chat) => { setSelectedChat(chat); setShowList(false); }} />
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col min-h-0 ${!showList ? 'block' : 'hidden md:flex'}`}>
          {!showList && (
            <button
              onClick={() => { setShowList(true); setSelectedChat(null); }}
              className="md:hidden p-3 flex items-center gap-2 text-[#9EABCE] text-sm border-b border-white/5"
              data-testid="chat-back-btn"
            >
              <ArrowLeft size={16} /> Back to messages
            </button>
          )}
          <ChatMessages
            chat={selectedChat}
            currentUserId={currentUser.id}
            onLimitHit={handleLimitHit}
            onChatCreated={(promotedChat) => {
              setSelectedChat(promotedChat);
              fetchChats();
              fetchLimitStatus();
            }}
          />
        </div>
      </div>
    </div>
  );
}
